import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Asset imports - bundler handles path resolution
import micMutedIcon from '~/static/assets/icons/landing-page/mic-muted-icon.svg';
import videoOffIcon from '~/static/assets/icons/landing-page/video-off-icon.svg';

type SettingsTab = 'devices' | 'profile' | 'calendar' | 'meetings' | 'terms';

interface DevicesTabProps {
  isOpen: boolean;
  activeTab: SettingsTab;
  selectedCamera: string;
  onChangeSelectedCamera: (deviceId: string) => void;
  selectedMicrophone: string;
  onChangeSelectedMicrophone: (deviceId: string) => void;
  selectedAudioOutput: string;
  onChangeSelectedAudioOutput: (deviceId: string) => void;
  startWithAudioMuted: boolean;
  onChangeStartWithAudioMuted: (v: boolean) => void;
  startWithVideoMuted: boolean;
  onChangeStartWithVideoMuted: (v: boolean) => void;
  onUserInteraction?: () => void;
}

const DevicesTab: React.FC<DevicesTabProps> = ({
  isOpen,
  activeTab,
  selectedCamera,
  onChangeSelectedCamera,
  selectedMicrophone,
  onChangeSelectedMicrophone,
  selectedAudioOutput,
  onChangeSelectedAudioOutput,
  startWithAudioMuted,
  onChangeStartWithAudioMuted,
  startWithVideoMuted,
  onChangeStartWithVideoMuted,
  onUserInteraction,
}) => {
  // Device lists (internal)
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [microphoneDevices, setMicrophoneDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState<boolean>(true);

  // Preview and audio refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastCameraIdRef = useRef<string | undefined>(undefined);
  const startSeqRef = useRef<number>(0);
  const testAudioRef = useRef<HTMLAudioElement>(null);
  const testOscTimeoutRef = useRef<number | null>(null);

  // UI state
  const [isPlayingTest, setIsPlayingTest] = useState<boolean>(false);

  const isActive = useMemo(() => activeTab === 'devices', [activeTab]);

  const formatLabel = (info: MediaDeviceInfo): string => {
    return (
      info.label ||
      (info.kind === 'audiooutput'
        ? 'Speaker'
        : info.kind === 'audioinput'
          ? 'Microphone'
          : 'Camera')
    );
  };

  // Initialize and enumerate devices
  const ensurePermissions = useCallback(async (): Promise<void> => {
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      for (const t of tmp.getTracks()) {
        t.stop();
      }
    } catch {
      // ignore
    }
  }, []);

  const enumerate = useCallback(async (): Promise<void> => {
    setIsLoadingDevices(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === 'videoinput');
      const mics = devices.filter((d) => d.kind === 'audioinput');
      const spks = devices.filter((d) => d.kind === 'audiooutput');

      const cameraList = cams.map((d) => ({ id: d.deviceId, label: d.label }));
      const micList = mics.map((d) => ({ id: d.deviceId, label: d.label }));
      const speakerList = spks.map((d) => ({ id: d.deviceId, label: d.label }));

      console.log('[DevicesTab] Enumerated devices:', {
        cameras: cameraList,
        microphones: micList,
        speakers: speakerList,
      });

      setCameraDevices(cams);
      setMicrophoneDevices(mics);
      setSpeakerDevices(spks);
    } catch {
      // ignore
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);

  // Validate selections and handle fallbacks when devices load
  useEffect(() => {
    if (isLoadingDevices) {
      return;
    }

    console.log('[DevicesTab] Validating device selections:', {
      camera: selectedCamera,
      microphone: selectedMicrophone,
      audioOutput: selectedAudioOutput,
    });

    // Seed a default when nothing is selected yet (SM-1187). The stale-id
    // fallbacks below only run once a selection exists, so an unseeded profile
    // would otherwise sit on "Select device" forever. This is the normal case
    // in hosted/MF mode: settings live on the TeamHub host origin, but the
    // meeting (which would populate them) runs in a separate popup origin, so
    // the host-side selections are never seeded.
    if (!selectedCamera && cameraDevices[0]?.deviceId) {
      console.log('[DevicesTab] No camera selected; defaulting to:', cameraDevices[0].label);
      onChangeSelectedCamera(cameraDevices[0].deviceId);
    }
    if (!selectedMicrophone && microphoneDevices.length > 0) {
      const nonWebcamMic = microphoneDevices.find(
        (d) =>
          !(d.label.toLowerCase().includes('webcam') || d.label.toLowerCase().includes('camera')),
      );
      const defaultMic = nonWebcamMic || microphoneDevices[0];
      if (defaultMic.deviceId) {
        console.log('[DevicesTab] No microphone selected; defaulting to:', defaultMic.label);
        onChangeSelectedMicrophone(defaultMic.deviceId);
      }
    }
    if (!selectedAudioOutput && speakerDevices[0]?.deviceId) {
      console.log('[DevicesTab] No audio output selected; defaulting to:', speakerDevices[0].label);
      onChangeSelectedAudioOutput(speakerDevices[0].deviceId);
    }

    // --- Camera Validation ---
    if (selectedCamera) {
      const foundCam = cameraDevices.find((d) => d.deviceId === selectedCamera);
      if (foundCam) {
        console.log('[DevicesTab] Selected camera FOUND in enumerated devices:', foundCam.label);
      } else {
        console.warn('[DevicesTab] Selected camera NOT FOUND in enumerated devices!', {
          selectedId: selectedCamera,
          availableIds: cameraDevices.map((d) => d.deviceId),
          availableLabels: cameraDevices.map((d) => d.label),
        });

        let matchedByLabel = false;
        try {
          const jitsiLs = localStorage.getItem('jitsiLocalStorage');
          if (jitsiLs) {
            const jitsiData = JSON.parse(jitsiLs);
            const baseSettings = jitsiData['features/base/settings'];
            if (baseSettings) {
              const settings = JSON.parse(baseSettings);
              const jitsiCameraLabel = settings.userSelectedCameraDeviceLabel;
              if (jitsiCameraLabel) {
                console.log(
                  '[DevicesTab] Trying to match camera by label from Jitsi:',
                  jitsiCameraLabel,
                );
                let matchedCamera = cameraDevices.find((d) => d.label === jitsiCameraLabel);
                if (!matchedCamera) {
                  matchedCamera = cameraDevices.find(
                    (d) =>
                      d.label.toLowerCase().includes(jitsiCameraLabel.toLowerCase()) ||
                      jitsiCameraLabel.toLowerCase().includes(d.label.toLowerCase()),
                  );
                }
                if (matchedCamera) {
                  console.log('[DevicesTab] Matched camera by label!', matchedCamera.label);
                  onChangeSelectedCamera(matchedCamera.deviceId);
                  matchedByLabel = true;
                }
              }
            }
          }
        } catch (e) {
          console.warn(
            '[DevicesTab] Error reading Jitsi localStorage for camera label matching:',
            e,
          );
        }

        if (!matchedByLabel && cameraDevices.length > 0) {
          console.log('[DevicesTab] Falling back to camera:', cameraDevices[0].label);
          onChangeSelectedCamera(cameraDevices[0].deviceId);
        }
      }
    }

    // --- Microphone Validation ---
    if (selectedMicrophone) {
      const foundMic = microphoneDevices.find((d) => d.deviceId === selectedMicrophone);
      if (foundMic) {
        console.log(
          '[DevicesTab] Selected microphone FOUND in enumerated devices:',
          foundMic.label,
        );
      } else {
        console.warn('[DevicesTab] Selected microphone NOT FOUND in enumerated devices!', {
          selectedId: selectedMicrophone,
          availableIds: microphoneDevices.map((d) => d.deviceId),
          availableLabels: microphoneDevices.map((d) => d.label),
        });

        let matchedByLabel = false;
        try {
          const jitsiLs = localStorage.getItem('jitsiLocalStorage');
          if (jitsiLs) {
            const jitsiData = JSON.parse(jitsiLs);
            const baseSettings = jitsiData['features/base/settings'];
            if (baseSettings) {
              const settings = JSON.parse(baseSettings);
              const jitsiMicLabel = settings.userSelectedMicDeviceLabel;
              if (jitsiMicLabel) {
                console.log('[DevicesTab] Trying to match by label from Jitsi:', jitsiMicLabel);
                let matchedMic = microphoneDevices.find((d) => d.label === jitsiMicLabel);
                if (!matchedMic) {
                  matchedMic = microphoneDevices.find(
                    (d) =>
                      d.label.toLowerCase().includes(jitsiMicLabel.toLowerCase()) ||
                      jitsiMicLabel.toLowerCase().includes(d.label.toLowerCase()),
                  );
                }
                if (matchedMic) {
                  console.log('[DevicesTab] Matched microphone by label!', matchedMic.label);
                  onChangeSelectedMicrophone(matchedMic.deviceId);
                  matchedByLabel = true;
                }
              }
            }
          }
        } catch (e) {
          console.warn('[DevicesTab] Error reading Jitsi localStorage for label matching:', e);
        }

        if (!matchedByLabel && microphoneDevices.length > 0) {
          const nonWebcamMic = microphoneDevices.find(
            (d) =>
              !(
                d.label.toLowerCase().includes('webcam') || d.label.toLowerCase().includes('camera')
              ),
          );
          const fallbackMic = nonWebcamMic || microphoneDevices[0];
          console.log('[DevicesTab] Falling back to microphone:', fallbackMic.label);
          onChangeSelectedMicrophone(fallbackMic.deviceId);
        }
      }
    }

    // --- Audio Output Validation ---
    if (selectedAudioOutput) {
      const foundSpk = speakerDevices.find((d) => d.deviceId === selectedAudioOutput);
      if (foundSpk) {
        console.log(
          '[DevicesTab] Selected audio output FOUND in enumerated devices:',
          foundSpk.label,
        );
      } else {
        console.warn('[DevicesTab] Selected audio output NOT FOUND in enumerated devices!', {
          selectedId: selectedAudioOutput,
          availableIds: speakerDevices.map((d) => d.deviceId),
          availableLabels: speakerDevices.map((d) => d.label),
        });

        let matchedByLabel = false;
        try {
          const jitsiLs = localStorage.getItem('jitsiLocalStorage');
          if (jitsiLs) {
            const jitsiData = JSON.parse(jitsiLs);
            const baseSettings = jitsiData['features/base/settings'];
            if (baseSettings) {
              const settings = JSON.parse(baseSettings);
              const jitsiSpkLabel = settings.userSelectedAudioOutputDeviceLabel;
              if (jitsiSpkLabel) {
                console.log(
                  '[DevicesTab] Trying to match audio output by label from Jitsi:',
                  jitsiSpkLabel,
                );
                let matchedSpk = speakerDevices.find((d) => d.label === jitsiSpkLabel);
                if (!matchedSpk) {
                  matchedSpk = speakerDevices.find(
                    (d) =>
                      d.label.toLowerCase().includes(jitsiSpkLabel.toLowerCase()) ||
                      jitsiSpkLabel.toLowerCase().includes(d.label.toLowerCase()),
                  );
                }
                if (matchedSpk) {
                  console.log('[DevicesTab] Matched audio output by label!', matchedSpk.label);
                  onChangeSelectedAudioOutput(matchedSpk.deviceId);
                  matchedByLabel = true;
                }
              }
            }
          }
        } catch (e) {
          console.warn('[DevicesTab] Error reading Jitsi localStorage for label matching:', e);
        }

        if (!matchedByLabel && speakerDevices.length > 0) {
          console.log('[DevicesTab] Falling back to audio output:', speakerDevices[0].label);
          onChangeSelectedAudioOutput(speakerDevices[0].deviceId);
        }
      }
    }
  }, [
    isLoadingDevices,
    cameraDevices,
    microphoneDevices,
    speakerDevices,
    selectedCamera,
    selectedMicrophone,
    selectedAudioOutput,
    onChangeSelectedCamera,
    onChangeSelectedMicrophone,
    onChangeSelectedAudioOutput,
  ]);

  // Handle initialization and cleanup
  useEffect(() => {
    if (isOpen && navigator.mediaDevices) {
      (async () => {
        await ensurePermissions();
        await enumerate();
      })();

      // Listen for device changes
      navigator.mediaDevices.addEventListener('devicechange', enumerate);
    }

    return () => {
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener('devicechange', enumerate);
      }

      // clean preview
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) {
          t.stop();
        }
        streamRef.current = null;
      }
      if (testOscTimeoutRef.current) {
        window.clearTimeout(testOscTimeoutRef.current);
        testOscTimeoutRef.current = null;
      }
    };
  }, [isOpen, ensurePermissions, enumerate]);

  // Broadcast default selections chosen here (first device found) so Jitsi can pick them if active
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    try {
      if (selectedCamera) {
        window.dispatchEvent(
          new CustomEvent('settings-devices-changed', {
            detail: { source: 'settings', cameraId: selectedCamera },
          }),
        );
      }
      if (selectedMicrophone) {
        window.dispatchEvent(
          new CustomEvent('settings-devices-changed', {
            detail: { source: 'settings', microphoneId: selectedMicrophone },
          }),
        );
      }
      if (selectedAudioOutput) {
        window.dispatchEvent(
          new CustomEvent('settings-devices-changed', {
            detail: { source: 'settings', audioOutputId: selectedAudioOutput },
          }),
        );
      }
    } catch {
      // Intentionally ignore
    }
    // Only when open or when any selection changes
  }, [isOpen, selectedCamera, selectedMicrophone, selectedAudioOutput]);

  // Start/attach camera preview (or clear when no camera selected)
  useEffect(() => {
    if (!(isOpen && isActive)) {
      return;
    }
    if (!selectedCamera) {
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) {
          t.stop();
        }
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      lastCameraIdRef.current = undefined;
      return;
    }

    const startPreview = async (deviceId: string): Promise<void> => {
      try {
        const targetId = deviceId;
        const currentTrack = streamRef.current?.getVideoTracks()[0];
        const isLive = Boolean(currentTrack && currentTrack.readyState === 'live');
        const sameCamera =
          isLive && lastCameraIdRef.current && lastCameraIdRef.current === targetId;
        if (sameCamera && streamRef.current) {
          // Attach existing stream
          if (videoRef.current) {
            videoRef.current.srcObject = streamRef.current;
            const v = videoRef.current;
            // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
            v.onloadedmetadata = () => v.play().catch(() => {});
            // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
            await v.play().catch(() => {});
          }
          return;
        }

        // Need a new stream: stop any old tracks first
        if (streamRef.current) {
          for (const t of streamRef.current.getTracks()) {
            t.stop();
          }
          streamRef.current = null;
        }

        const base: MediaStreamConstraints = {
          video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
          // biome-ignore lint/suspicious/noExplicitAny: MediaStreamConstraints cast needed for browser compatibility
        } as any;

        let stream: MediaStream | null = null;
        const seq = ++startSeqRef.current;
        try {
          stream = await navigator.mediaDevices.getUserMedia(base);
          // biome-ignore lint/suspicious/noExplicitAny: browser MediaDevices error types are not well typed
        } catch (e: any) {
          // Fallbacks for busy/overconstrained devices
          if (
            e &&
            (e.name === 'NotReadableError' ||
              e.name === 'OverconstrainedError' ||
              e.name === 'NotFoundError')
          ) {
            try {
              stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            } catch {
              stream = null;
            }
          }
        }
        if (stream) {
          // If another start began, discard this stream
          if (seq !== startSeqRef.current) {
            for (const t of stream.getTracks()) {
              t.stop();
            }
            return;
          }
          streamRef.current = stream;
          const v = videoRef.current;
          if (v) {
            v.srcObject = stream;
            v.muted = true;
            // biome-ignore lint/suspicious/noExplicitAny: playsInline is not in HTMLVideoElement types
            v.playsInline = true as any;
            // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
            v.onloadedmetadata = () => v.play().catch(() => {});
            // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
            await v.play().catch(() => {});
          }
          try {
            const track = stream.getVideoTracks()[0];
            const settings = track?.getSettings?.() as MediaTrackSettings | undefined;
            lastCameraIdRef.current = (settings?.deviceId as string | undefined) || targetId;
          } catch {
            // Intentionally ignore
          }
        }
      } catch {
        // Intentionally ignore
      }
    };

    startPreview(selectedCamera);
  }, [isOpen, isActive, selectedCamera]);

  // Restart preview when selected camera changes while active
  useEffect(() => {
    if (!(isOpen && isActive && selectedCamera)) {
      return;
    }
    // Stop previous stream and restart via the other effect
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) {
        t.stop();
      }
      streamRef.current = null;
    }
  }, [selectedCamera, isOpen, isActive]);

  const playTestSound = useCallback(async () => {
    setIsPlayingTest(true);
    const el = testAudioRef.current;
    if (!el) {
      setIsPlayingTest(false);
      return;
    }
    try {
      const win = window as Window & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const Ac = win.AudioContext || win.webkitAudioContext;
      if (!Ac) {
        return;
      }
      const ac = new Ac();
      const destination = ac.createMediaStreamDestination();
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      const now = ac.currentTime;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(0.2, now + 0.02);
      gain.gain.setTargetAtTime(0.0, now + 0.45, 0.05);
      osc.connect(gain).connect(destination);

      // Route to selected sink if supported
      if (typeof el.setSinkId === 'function') {
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow setSinkId errors
        await el.setSinkId(selectedAudioOutput || 'default').catch(() => {});
      }

      el.srcObject = destination.stream;
      el.volume = 1;
      el.muted = false;
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
      await el.play().catch(() => {});
      osc.start();

      testOscTimeoutRef.current = window.setTimeout(() => {
        try {
          osc.stop();
          el.pause();
          el.srcObject = null;
          ac.close();
        } catch {
          // Intentionally ignore
        }
        setIsPlayingTest(false);
        testOscTimeoutRef.current = null;
      }, 700);
    } catch {
      setIsPlayingTest(false);
    }
  }, [selectedAudioOutput]);

  return (
    <div className="settingsTabContent devicesLayout">
      <div className="devicesLeft">
        <div className="cameraPreview">
          <div className="cameraPreviewFrame">
            <div className="cameraPreviewContent">
              {!(isLoadingDevices || selectedCamera) ? (
                <span className="cameraPreviewUnavailable">Select a camera</span>
              ) : (
                <video
                  ref={videoRef}
                  className="cameraFeed"
                  autoPlay
                  playsInline
                  muted
                  onCanPlay={() => {
                    try {
                      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally swallow play errors
                      videoRef.current?.play().catch(() => {});
                    } catch {
                      // Intentionally ignore
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="meetingOptions devicesToggleOptions">
          <div className="meetingOption">
            <div className="optionLabel">
              <img
                src={micMutedIcon.src}
                width={micMutedIcon.width}
                height={micMutedIcon.height}
                alt="Audio muted"
                className="optionIcon"
              />
              Start with audio muted
            </div>
            <label className="toggleSwitch">
              <input
                type="checkbox"
                checked={startWithAudioMuted}
                onChange={(e) => {
                  onUserInteraction?.();
                  onChangeStartWithAudioMuted(e.target.checked);
                }}
              />
              <span className="toggleSlider" />
            </label>
          </div>

          <div className="meetingOption">
            <div className="optionLabel">
              <img
                src={videoOffIcon.src}
                width={videoOffIcon.width}
                height={videoOffIcon.height}
                alt="Video off"
                className="optionIcon"
              />
              Start with video off
            </div>
            <label className="toggleSwitch">
              <input
                type="checkbox"
                checked={startWithVideoMuted}
                onChange={(e) => {
                  onUserInteraction?.();
                  onChangeStartWithVideoMuted(e.target.checked);
                }}
              />
              <span className="toggleSlider" />
            </label>
          </div>
        </div>
      </div>

      <div className="deviceSettings devicesRight">
        <div className="deviceSetting">
          <label className="deviceLabel" htmlFor="camera-select">
            Camera
          </label>
          <select
            id="camera-select"
            value={isLoadingDevices ? '' : selectedCamera}
            onChange={(e) => {
              onUserInteraction?.();
              onChangeSelectedCamera(e.target.value);
            }}
            className="deviceSelect"
            disabled={isLoadingDevices}
          >
            {isLoadingDevices ? (
              <option value="">Initializing...</option>
            ) : (
              <>
                {!selectedCamera && <option value="">Select device</option>}
                {cameraDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {formatLabel(d)}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        <div className="deviceSetting">
          <label className="deviceLabel" htmlFor="microphone-select">
            Microphone
          </label>
          <select
            id="microphone-select"
            value={isLoadingDevices ? '' : selectedMicrophone}
            onChange={(e) => {
              onUserInteraction?.();
              onChangeSelectedMicrophone(e.target.value);
            }}
            className="deviceSelect"
            disabled={isLoadingDevices}
          >
            {isLoadingDevices ? (
              <option value="">Initializing...</option>
            ) : (
              <>
                {!selectedMicrophone && <option value="">Select device</option>}
                {microphoneDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {formatLabel(d)}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        <div className="deviceSetting">
          <label className="deviceLabel" htmlFor="audio-output-select">
            Audio output
          </label>
          <select
            id="audio-output-select"
            value={isLoadingDevices ? '' : selectedAudioOutput}
            onChange={(e) => {
              onUserInteraction?.();
              onChangeSelectedAudioOutput(e.target.value);
            }}
            className="deviceSelect"
            disabled={isLoadingDevices}
          >
            {isLoadingDevices ? (
              <option value="">Initializing...</option>
            ) : (
              <>
                {!(selectedAudioOutput && speakerDevices?.length) && (
                  <option value="">Select device</option>
                )}
                {speakerDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {formatLabel(d)}
                  </option>
                ))}
              </>
            )}
          </select>
          <button
            type="button"
            onClick={playTestSound}
            className="testSoundLink"
            disabled={isPlayingTest}
          >
            {isPlayingTest ? 'Playing…' : 'Play a test sound'}
          </button>
          {/* biome-ignore lint/a11y/useMediaCaption: programmatic test tone, no speech content to caption */}
          <audio ref={testAudioRef} aria-label="Test sound" />
        </div>
      </div>
    </div>
  );
};

export { DevicesTab };
